import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ProductCard from '@/components/feature/ProductCard';
import SEOSchema from '@/components/feature/SEOSchema';
import { categories } from '@/mocks/categories';
import { getProductsByCategory } from '@/mocks/products';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const decodedSlug = slug ? decodeURIComponent(slug) : '';
  const currentCategory = categories.find(c => c.slug === decodedSlug);
  const products = getProductsByCategory(decodedSlug);

  if (!currentCategory) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 pb-10 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-semibold text-foreground-950 mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="WebPage" data={{
        name: `${currentCategory.name} | 久久精品`,
        description: currentCategory.description,
        inLanguage: 'zh-CN',
        isPartOf: { '@type': 'WebSite', name: '久久精品', url: 'https://jiujiuboutique.com' },
      }} />
      <SEOSchema type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jiujiuboutique.com/' },
          { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `https://jiujiuboutique.com/category/${encodeURIComponent(currentCategory.slug)}` },
        ],
      }} />

      <Navbar />
      <main className="min-h-screen pt-20">
        <Breadcrumb items={[{ label: currentCategory.name }]} />

        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-3">
                {currentCategory.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
                {currentCategory.description}
              </p>
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-lg font-heading font-semibold text-foreground-950">
                共 {products.length} 件好物
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-500">其他分类：</span>
                {categories.filter(c => c.slug !== decodedSlug).map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${encodeURIComponent(cat.slug)}`}
                    className="px-3 py-1.5 text-xs font-medium whitespace-nowrap rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {products.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-foreground-500 text-sm">暂无该分类的商品，敬请期待更多好物上架。</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}